import { Clock3, ExternalLink, Mail, MapPin, Phone, ShieldCheck, UploadCloud } from "lucide-react";
import { CONTACT, IMAGES } from "@/data/site";
import { Button } from "@/components/ui/button";
import { ConsultationForm } from "@/components/ConsultationForm";
import { PageHero } from "@/components/PageHero";
import { Reveal, ScaleIn } from "@/components/Motion";

const contactCards = [
  { icon: Phone, title: "Call Our Office", value: CONTACT.phone, href: CONTACT.phoneHref, testId: "contact-phone-card" },
  { icon: Mail, title: "Email", value: CONTACT.email, href: CONTACT.emailHref, testId: "contact-email-card" },
  { icon: MapPin, title: "Office", value: CONTACT.address, href: "https://maps.google.com/?q=21031%20International%20Blvd%20Suite%2012%20SeaTac%20WA%2098198", testId: "contact-address-card" },
];

export default function Contact() {
  return (
    <>
      <PageHero
        eyebrow="Contact & Scheduling"
        titleLines={["Schedule an", "Immigration Consultation", "With Egaal"]}
        description="Tell us what you are working on, choose an available consultation time, submit supporting documents through Dropbox, or call the office directly. We will help you organize the next step."
        image={IMAGES.consultation}
        imageAlt="Consultation at Egaal office"
      />
      <section className="section-padding" data-testid="contact-methods-section">
        <div className="section-shell grid gap-6 lg:grid-cols-3">
          {contactCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <ScaleIn key={card.title} delay={index * 0.08}>
                <a
                  href={card.href}
                  target={card.href.startsWith("http") ? "_blank" : undefined}
                  rel={card.href.startsWith("http") ? "noreferrer" : undefined}
                  data-testid={card.testId}
                  className="group block h-full rounded-lg bg-white p-7 shadow-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-editorial"
                >
                  <Icon className="h-8 w-8 text-brand-copper" />
                  <h2 className="mt-7 font-serif text-2xl font-bold">{card.title}</h2>
                  <p className="mt-3 break-words text-sm font-bold leading-7 text-brand-navy/68 group-hover:text-brand-navy">{card.value}</p>
                </a>
              </ScaleIn>
            );
          })}
        </div>
      </section>
      <section className="bg-white section-padding" data-testid="contact-form-section">
        <div className="section-shell grid gap-10 lg:grid-cols-[1.15fr_0.85fr]">
          <Reveal className="min-w-0">
            <ConsultationForm />
          </Reveal>
          <div className="min-w-0 space-y-8">
            <Reveal delay={0.1}>
              <div className="rounded-lg border border-brand-navy/10 bg-white p-7 shadow-editorial" data-testid="dropbox-document-intake">
                <span className="grid h-12 w-12 place-items-center rounded-lg bg-brand-cream text-brand-navy">
                  <UploadCloud className="h-5 w-5" />
                </span>
                <h3 className="mt-6 font-serif text-2xl font-bold">Secure Document Intake</h3>
                <p className="mt-3 text-sm leading-7 text-brand-navy/65">
                  Submit immigration, tax, translation, and supporting documents through Egaal’s Dropbox request. The Dropbox page opens in a new tab.
                </p>
                <Button asChild size="lg" className="mt-7 w-full rounded-full bg-brand-navy text-white hover:bg-brand-ink">
                  <a
                    href="https://www.dropbox.com/request/ZRmj3nquMd7thSr03ELz"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="secure-document-intake-dropbox-button"
                  >
                    Submit Your Documents <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </Reveal>
            <Reveal delay={0.18} className="rounded-lg bg-brand-cream p-7">
              <Clock3 className="h-8 w-8 text-brand-copper" />
              <h2 className="mt-6 font-serif text-2xl font-bold">Business Hours</h2>
              <div className="mt-5 space-y-4">
                {CONTACT.hours.map((item) => (
                  <div key={item.days} className="flex items-start justify-between gap-4 border-b border-brand-navy/10 pb-4 last:border-b-0">
                    <span className="text-sm font-bold text-brand-navy/70">{item.days}</span>
                    <span className="text-right text-sm font-extrabold">{item.time}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <section className="section-padding" data-testid="contact-preparation-section">
        <div className="section-shell grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <Reveal>
            <p className="overline text-brand-copper">Before You Visit</p>
            <h2 className="mt-4 font-serif text-3xl font-bold sm:text-4xl">What to bring to your consultation.</h2>
            <Button asChild className="mt-8 rounded-full bg-brand-navy px-6 text-white hover:bg-brand-ink">
              <a
                href="https://www.dropbox.com/request/ZRmj3nquMd7thSr03ELz"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="document-checklist-dropbox-button"
              >
                Submit Documents via Dropbox <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2">
            {["Passports and photo identification", "USCIS, NVC, or embassy notices", "Prior applications and receipts", "Birth, marriage, or divorce records", "Court or police records if applicable", "Questions, deadlines, and goals"].map((item, index) => (
              <Reveal key={item} delay={index * 0.05} className="flex items-center gap-4 rounded-lg bg-white p-5 shadow-sm">
                <ShieldCheck className="h-5 w-5 shrink-0 text-brand-copper" />
                <span className="text-sm font-bold">{item}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
